<template>
  <div class="auth-container">
    <VContainer
      class="h-100 w-100"
      :class="!isDisplayedImageAndTitle && 'pa-0'"
      fluid
    >
      <VRow dense justify="center">
        <VCol
          :cols="isDisplayedImageAndTitle && '12'"
          :xl="isDisplayedImageAndTitle && '5'"
          :lg="isDisplayedImageAndTitle && '5'"
          :md="isDisplayedImageAndTitle && '8'"
          :sm="isDisplayedImageAndTitle && '12'"
          :xs="isDisplayedImageAndTitle && '12'"
        >
          <VContainer class="h-100" fluid>
            <VRow justify="center">
              <VCol
                v-if="isDisplayedImageAndTitle"
                class="d-flex align-items-center flex-column px-2 pb-0"
                cols="12"
                col="12"
              >
                <div class="py-3 pb-0">
                  <div
                    class="
                      img-container
                      d-flex
                      align-items-center
                      justify-content-center
                    "
                  >
                    <img
                      :src="
                        require(`@slg/web-customer-shared/src/assets/img/${getCurrentImageFromStep}.svg`)
                      "
                      :alt="getCurrentTitleFromStep"
                    />
                  </div>
                  <div class="tittle-container pt-6 text-center">
                    <span class="heading-5 textPrimary--text">
                      {{ getCurrentTitleFromStep }}
                    </span>
                  </div>
                </div>
              </VCol>
            </VRow>
            <keepAlive>
              <component
                :is="currentStepComponent"
                v-bind="nightCardStepProps"
                :location="location"
                :class="isDisplayedImageAndTitle && 'pt-6'"
                :receivedRemainingTime="receivedRemainingTime"
                :otpExpiredAt="otpExpiredAt"
                :phone="customerDetails.phone"
                :receivedErrorCode="errorCode"
                :attemptsIncorrectSingIn="attemptsIncorrectSingIn"
                :termsAndConditionsPageName="termsAndConditionsPageName"
                :privacyPolicyPageName="privacyPolicyPageName"
                @checkPhone="checkPhone"
                @toSignUpStep="toSignUpStep"
                @toSignInStep="toSignInStep"
                @toStartStep="toStartStep"
                @toVerificationStep="toVerificationStep"
                @toBlockedAuth="toBlockedAuth"
                @toResetPassword="toResetPassword"
                @sendVerificationCode="sendVerificationCode"
                @recoverPassword="recoverPassword"
                @sendVerificationCodeForExistingUser="
                  sendVerificationCodeForExistingUser
                "
                @registration="registration"
                @changePassword="changePassword"
                @createPassword="changePassword"
                @trackGtmEventForUserCreatedByOperator="
                  $emit('trackGtmEventForUserCreatedByOperator')
                "
                @trackGtmEventForNonRegisteredUser="
                  $emit('trackGtmEventForNonRegisteredUser')
                "
                @signIn="signIn"
                @setGlobalLoading="$emit('setGlobalLoading', isLoading)"
                @toVerificationRegisteredUserStep="
                  toVerificationRegisteredUserStep
                "
                @skip="onNightCardSignUpSkipped"
                @continue="onNightCardSignUpContinue"
                @back="fromNightCardBackToSignUp"
              />
            </keepAlive>
            <STurnstileManaged />
            <VRow
              v-if="showSignUpStepIndicator"
              justify="center"
              class="pt-2 pb-4"
            >
              <VCol cols="12" class="text-center px-2">
                <span
                  class="body-2 textSecondary--text"
                  data-test="sign-up-step-indicator"
                >
                  {{ signUpStepIndicatorLabel }}
                </span>
              </VCol>
            </VRow>
          </VContainer>
        </VCol>
      </VRow>
    </VContainer>
  </div>
</template>

<script lang="ts">
export default {
  name: 'SAuthForm',
};
</script>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import UserService from '@slg/web-customer-shared/src/services/UserService';
import AuthErrorsEnum from '@slg/web-customer-shared/src/enum/AuthErrorsEnum';
import { emitNightCardLinkError } from '@slg/web-customer-shared/src/utils/nightCardLinkErrorEvents';
import { withNightCardNameFallback } from '@slg/web-customer-shared/src/utils/nightCardLinkHelpers';
import resolveNightCardProgramInfoUrl from '@slg/web-customer-shared/src/utils/resolveNightCardProgramInfoUrl';
import { useGlobalErrorDialog, useI18n, useRouter, useVm } from '../../composables';
import StartStep from './StartStep.vue';
import SignUpStep from './signUp/SignUpStep.vue';
import SignUpNightCardStep from './signUp/SignUpNightCardStep.vue';
import SignInStep from './signIn/SignInStep.vue';
import VerificationStep from './signUp/VerificationStep.vue';
import BlockedAuth from './BlockedAuth.vue';
import ResetPasswordStep from './resetPassword/ResetPasswordStep.vue';
import ChangePasswordStep from './resetPassword/ChangePasswordStep.vue';
import VerificationRegisteredUserStep from './createPassword/VerificationRegisteredUserStep.vue';
import CreationPasswordStep from './createPassword/CreationPasswordStep.vue';
import STurnstileManaged from './STurnstileManaged.vue';
import { isManagedCaptchaBlockingRequest } from '../../utils/turnstile';

const props = defineProps({
  isDisplayedImageAndTitle: {
    type: Boolean,
    default: true,
  },
  isAuthPage: {
    type: Boolean,
    default: true,
  },
  termsAndConditionsPageName: {
    type: String,
    default: null,
  },
  privacyPolicyPageName: {
    type: String,
    default: null,
  },
  lang: {
    type: String,
    default: null,
  },
  location: {
    type: String,
    default: null,
  },
  appConfig: {
    type: Object,
    default: () => ({ isNightCardsSupport: false }),
  },
});

const emit = defineEmits([
  'setGlobalLoading',
  'trackGtmEventForRegisteredUser',
  'setAuthToken',
  'populateUserProfileInfo',
  'redirect',
  'trackGtmEventForUserCreatedByOperator',
  'trackGtmEventForNonRegisteredUser',
]);

// Keep the spinner off while the user may need to solve the managed captcha,
// and bring it back once the captcha is accepted and the request is sent.
watch(isManagedCaptchaBlockingRequest, (isBlocking) => {
  emit('setGlobalLoading', !isBlocking);
}, { flush: 'sync' });

const { setGlobalError } = useGlobalErrorDialog();
const { t, locale } = useI18n();
const router = useRouter();
const vm = useVm();

const currentStep = ref('StartStep');
const customerDetails = ref<any>({});
const authSteps = {
  startStep: 'StartStep',
  signUpStep: 'SignUpStep',
  signUpNightCardStep: 'SignUpNightCardStep',
  signInStep: 'SignInStep',
  verificationStep: 'VerificationStep',
  blockedAuth: 'BlockedAuth',
  resetPassword: 'ResetPasswordStep',
  changePassword: 'ChangePasswordStep',
  verificationRegisteredUserStep: 'VerificationRegisteredUserStep',
  creationPasswordStep: 'CreationPasswordStep',
};
const stepComponents = {
  StartStep,
  SignUpStep,
  SignUpNightCardStep,
  SignInStep,
  VerificationStep,
  BlockedAuth,
  ResetPasswordStep,
  ChangePasswordStep,
  VerificationRegisteredUserStep,
  CreationPasswordStep,
};
const currentStepComponent = computed(() => stepComponents[currentStep.value]);
const receivedRemainingTime = ref<any>(null);
const otpExpiredAt = ref<any>(null);
const errorCode = ref<any>('');
const attemptsIncorrectSingIn = ref(0);
const authToken = ref<any>(null);

const nightCardStepProps = computed(() => {
  if (currentStep.value !== authSteps.signUpNightCardStep) {
    return {};
  }
  return {
    infoLinkUrl: resolveNightCardProgramInfoUrl(
      (props.appConfig as any)?.nightCardProgramInfoUrl || '',
      locale.value,
      props.appConfig,
    ),
  };
});

const getCurrentTitleFromStep = computed(() => {
  if (
    currentStep.value === authSteps.signUpStep
    || currentStep.value === authSteps.signUpNightCardStep
    || currentStep.value === authSteps.verificationStep
    || currentStep.value === authSteps.blockedAuth
  ) {
    return t('authForm.signUp');
  }

  if (currentStep.value === authSteps.signInStep) {
    return t('authForm.enterPassword');
  }

  if (
    currentStep.value === authSteps.resetPassword
    || currentStep.value === authSteps.changePassword
  ) {
    return t('authForm.resetPassword');
  }

  if (currentStep.value === authSteps.verificationRegisteredUserStep) {
    return t('authForm.signUp');
  }

  if (currentStep.value === authSteps.creationPasswordStep) {
    return t('authForm.creationPassword');
  }

  return `${t('authForm.logIn')} / ${t('authForm.signUp')}`;
});

const getCurrentImageFromStep = computed(() => (
  currentStep.value !== authSteps.blockedAuth
    ? 'auth-logo'
    : 'blocked-auth'
));

const showSignUpStepIndicator = computed(() => {
  if (!(props.appConfig as any)?.isNightCardsSupport) {
    return false;
  }
  return (
    currentStep.value === authSteps.signUpStep
    || currentStep.value === authSteps.signUpNightCardStep
  );
});

const signUpStepIndicatorLabel = computed(() => {
  const current = currentStep.value === authSteps.signUpStep ? 1 : 2;
  return t('authForm.signUpStepIndicator', { current, total: 2 });
});

function toDeactivatedUserPage() {
  router.push({
    name: 'DeactivatedUser',
    params: { isAuthFailed: true },
  });
}

function toSignUpStep(number: any) {
  customerDetails.value.phone = number || customerDetails.value.phone;
  currentStep.value = authSteps.signUpStep;
}

function toSignInStep(number: any) {
  customerDetails.value.phone = number;
  currentStep.value = authSteps.signInStep;
}

function toStartStep() {
  currentStep.value = authSteps.startStep;
}

function toBlockedAuth() {
  currentStep.value = authSteps.blockedAuth;
}

function clearNightCardLinkDetails() {
  delete customerDetails.value.nightRiderCardDetailsFormDTO;
}

function handleNightCardRegistrationError(error: any) {
  if (!(props.appConfig as any)?.isNightCardsSupport) {
    return false;
  }
  if (!customerDetails.value.nightRiderCardDetailsFormDTO) {
    return false;
  }
  if (error?.status !== 400) {
    return false;
  }
  emitNightCardLinkError(vm, error);
  currentStep.value = authSteps.signUpNightCardStep;
  return true;
}

async function signIn(signInData: any) {
  emit('setGlobalLoading', true);
  emit('trackGtmEventForRegisteredUser');
  try {
    const { data } = await UserService.signIn(signInData);
    emit('setAuthToken', data.access_token);
    emit('populateUserProfileInfo');
    emit('redirect');
  } catch (e) {
        if ((e as any).errorCode === AuthErrorsEnum.CUSTOMER_ACCOUNT_HAS_BEEN_SUSPENDED) {
      toDeactivatedUserPage();
    } else {
      attemptsIncorrectSingIn.value += 1;
    }
  }
  emit('setGlobalLoading', false);
}

async function sendVerificationCode() {
  emit('setGlobalLoading', true);
  await UserService.verificationPhoneNumber(
    customerDetails.value.phone,
    props.lang,
  )
    .then((response) => {
      if (receivedRemainingTime.value === 0) {
        currentStep.value = authSteps.blockedAuth;
      } else {
        otpExpiredAt.value = response.data.otpExpiredAt;
        receivedRemainingTime.value = response.data.nextGenerationDelaySec;
        errorCode.value = '';
        currentStep.value = authSteps.verificationStep;
      }
    })
    .catch(async (error: any) => {
      if (error.message?.includes('86400')) {
        currentStep.value = authSteps.blockedAuth;
      } else if (
        error
        && error.errorCode
          === AuthErrorsEnum.OTP_TOKEN_IS_ALREADY_SENT_AND_NEXT_GENERATION_IS_LOCKED
      ) {
        currentStep.value = authSteps.verificationStep;
        errorCode.value = error.errorCode;
      } else if (
        error
        && error.errorCode
          === AuthErrorsEnum.CUSTOMER_USER_REGISTRY_FAILED_OTP_IS_NOT_VALID_OR_EXPIRED
      ) {
        errorCode.value = error.errorCode;
      } else {
        await setGlobalError(error, false);
      }
    });
  emit('setGlobalLoading', false);
}

async function sendVerificationCodeForExistingUser(isVerifyingUser = false) {
  emit('setGlobalLoading', true);
  await UserService.forgotPassword(customerDetails.value.phone)
    .then((response) => {
      if (receivedRemainingTime.value === 0) {
        currentStep.value = authSteps.blockedAuth;
      } else {
        otpExpiredAt.value = response.data.otpExpiredAt;
        receivedRemainingTime.value = response.data.nextGenerationDelaySec;
        errorCode.value = null;
        currentStep.value = isVerifyingUser
          ? authSteps.verificationRegisteredUserStep
          : authSteps.resetPassword;
      }
    })
    .catch(async (error: any) => {
      if (error.errorCode === AuthErrorsEnum.CUSTOMER_ACCOUNT_HAS_BEEN_SUSPENDED) {
        toDeactivatedUserPage();
      }

      if (error.message?.includes('86400')) {
        currentStep.value = authSteps.blockedAuth;
      } else if (
        error
        && error.errorCode
          === AuthErrorsEnum.OTP_TOKEN_IS_ALREADY_SENT_AND_NEXT_GENERATION_IS_LOCKED
      ) {
        errorCode.value = error.errorCode;
        currentStep.value = isVerifyingUser
          ? authSteps.verificationRegisteredUserStep
          : authSteps.resetPassword;
      } else if (
        error
        && (error.errorCode === AuthErrorsEnum.LOGIN_FAILED_INCORRECT_PHONE_OR_OTP
          || error.errorCode
            === AuthErrorsEnum.CUSTOMER_USER_REGISTRY_FAILED_OTP_IS_NOT_VALID_OR_EXPIRED)
      ) {
        errorCode.value = error.errorCode;
      } else if (error.errorCode === AuthErrorsEnum.ERR_INTERNAL_SERVER) {
        await setGlobalError(error);
      }
    });
  emit('setGlobalLoading', false);
}

async function checkPhone(phone: any) {
  emit('setGlobalLoading', true);
  try {
    const { data } = await UserService.phoneChecker(phone);
    if (data.phoneRegistered && data.passwordLogin) {
      emit('trackGtmEventForRegisteredUser');
      toSignInStep(phone);
    } else if (data.phoneRegistered && !data.passwordLogin) {
      emit('trackGtmEventForUserCreatedByOperator');
      await toVerificationRegisteredUserStep(phone, true);
    } else {
      emit('trackGtmEventForNonRegisteredUser');
      toSignUpStep(phone);
    }
    customerDetails.value.phone = phone;
  } catch (error: any) {
    await setGlobalError(error, false);
  }
  emit('setGlobalLoading', false);
}

async function toVerificationRegisteredUserStep(number: any, isVerifyingUser = false) {
  customerDetails.value.phone = number;
  await sendVerificationCodeForExistingUser(isVerifyingUser);
}

async function toResetPassword() {
  await sendVerificationCodeForExistingUser();
}

async function toVerificationStep(details: any) {
  customerDetails.value = { ...customerDetails.value, ...details };
  clearNightCardLinkDetails();
  if ((props.appConfig as any)?.isNightCardsSupport) {
    currentStep.value = authSteps.signUpNightCardStep;
  } else {
    await sendVerificationCode();
  }
}

async function registration(verificationCode: any, attempts: any) {
  emit('setGlobalLoading', true);
  customerDetails.value.otp = verificationCode;
  customerDetails.value.customerAppType = 'WEB';

  const register = async (registrationPayload) => {
    const response = await UserService.registration(registrationPayload);
    emit('setAuthToken', response.data.access_token);
    emit('populateUserProfileInfo');
    if (props.isAuthPage) {
      emit('redirect');
    }
    return response;
  };

  const handleRegistrationError = (error: any) => {
    const maxAttempts = 6;
    const secondsInDay = 86400;
    if (
      attempts >= maxAttempts
      && receivedRemainingTime.value === secondsInDay
    ) {
      currentStep.value = authSteps.blockedAuth;
    } else if (
      error
      && error.errorCode
        === AuthErrorsEnum.CUSTOMER_USER_REGISTRY_FAILED_OTP_IS_NOT_VALID_OR_EXPIRED
    ) {
      errorCode.value = error.errorCode;
    } else if (handleNightCardRegistrationError(error)) {
      // user returned to night card step with inline message
    } else {
      setGlobalError(error);
    }
  };

  const nightCardDto = customerDetails.value.nightRiderCardDetailsFormDTO;

  try {
    if (nightCardDto?.firstName && nightCardDto?.lastName) {
      await withNightCardNameFallback({
        firstName: nightCardDto.firstName,
        lastName: nightCardDto.lastName,
        request: ({ firstName, lastName }: any) => register({
          ...customerDetails.value,
          nightRiderCardDetailsFormDTO: {
            ...nightCardDto,
            firstName,
            lastName,
          },
        }),
      });
    } else {
      await register(customerDetails.value);
    }
  } catch (error: any) {
    handleRegistrationError(error);
  }

  emit('setGlobalLoading', false);
}

async function onNightCardSignUpSkipped() {
  clearNightCardLinkDetails();
  await sendVerificationCode();
}

async function onNightCardSignUpContinue({ nightRiderCardDetailsFormDTO }: any) {
  customerDetails.value = {
    ...customerDetails.value,
    nightRiderCardDetailsFormDTO,
  };
  await sendVerificationCode();
}

function fromNightCardBackToSignUp() {
  clearNightCardLinkDetails();
  currentStep.value = authSteps.signUpStep;
}

async function recoverPassword(
  verificationCode: any,
  attempts: any,
  isCreationPassword = false,
) {
  emit('setGlobalLoading', true);
  const details = {
    customerAppType: 'WEB',
    otp: verificationCode,
    phone: customerDetails.value.phone,
  };

  await UserService.signInByOtp(details)
    .then(async (response) => {
      authToken.value = response.data.access_token;
      currentStep.value = isCreationPassword
        ? authSteps.creationPasswordStep
        : authSteps.changePassword;
    })
    .catch((error: any) => {
      const maxAttempts = 6;
      const secondsInDay = 86400;

      if (error.errorCode === AuthErrorsEnum.CUSTOMER_ACCOUNT_HAS_BEEN_SUSPENDED) {
        toDeactivatedUserPage();
        return;
      }

      if (
        attempts >= maxAttempts
        && receivedRemainingTime.value === secondsInDay
      ) {
        currentStep.value = authSteps.blockedAuth;
      }

      if (
        error
        && error.errorCode === AuthErrorsEnum.LOGIN_FAILED_INCORRECT_PHONE_OR_OTP
      ) {
        errorCode.value = error.errorCode;
      } else {
        setGlobalError(error);
      }
    });

  emit('setGlobalLoading', false);
}

async function changePassword(password: any) {
  emit('setGlobalLoading', true);
  emit('setAuthToken', authToken.value);
  emit('populateUserProfileInfo');
  emit('redirect');
  try {
    await UserService.updateProfile({ password });
  } catch (e: any) {
    await setGlobalError(e);
  }
  emit('setGlobalLoading', false);
}
</script>